import { FunctionComponent, useEffect, useMemo } from 'react';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  MenuItem,
  TextField,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../Redux/Store/Hooks';
import {
  addDisplayDevice,
  clearAddDisplayDeviceState,
} from '../../Redux/Slicers/DisplayDevice/AddDisplayDeviceSlicer';
import {
  clearUpdateDisplayDeviceState,
  updateDisplayDevice,
} from '../../Redux/Slicers/DisplayDevice/UpdateDisplayDeviceSlicer';
import DiagramPicker from '../../Components/DiagramPicker/DiagramPicker';

interface DisplayFormProps {
  masterForm: (data: FormikProps<any>) => void;
}

const DEVICE_TYPES = ['tkinter', 'led', 'camera', 'kiosk', 'fixture', 'unknown'];
const DEVICE_MODES = ['entrance', 'level', 'aisle'];
const CAPABILITIES = ['screenshot', 'snapshot', 'counts', 'transaction', 'sensor'];

const DisplayForm: FunctionComponent<DisplayFormProps> = ({ masterForm }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const locationId = pathParts[pathParts.length - 1];

  const { updateableDate } = useAppSelector(
    (state: any) => state.UpdateDisplayDevice
  );
  const { data: levelsData } = useAppSelector((state: any) => state.Slot);
  const levels: any[] = levelsData?.response?.data ?? [];

  const isUpdate = updateableDate && Object.keys(updateableDate).length !== 0;

  const formik = useFormik({
    initialValues: {
      device_id: '',
      label: '',
      type: 'tkinter',
      mode: 'entrance',
      level_id: '',
      aisle_id: '',
      x: 0,
      y: 0,
      capabilities: ['counts'] as string[],
      ip: '',
    },
    validationSchema: Yup.object({
      device_id: Yup.string().required('Device ID is required'),
      label: Yup.string().required('Label is required'),
      type: Yup.string().required(),
      mode: Yup.string().required(),
    }),
    onSubmit: (values) => {
      const payload = { ...values, location_id: locationId };
      if (isUpdate) {
        dispatch(updateDisplayDevice({ ...payload, id: updateableDate._id }));
      } else {
        dispatch(addDisplayDevice(payload));
      }
    },
  });

  useEffect(() => {
    masterForm(formik);
    if (isUpdate) {
      formik.setFieldValue('device_id', updateableDate.device_id ?? '');
      formik.setFieldValue('label', updateableDate.label ?? '');
      formik.setFieldValue('type', updateableDate.type ?? 'tkinter');
      formik.setFieldValue('mode', updateableDate.mode ?? 'entrance');
      formik.setFieldValue('level_id', updateableDate.level_id?._id ?? updateableDate.level_id ?? '');
      formik.setFieldValue('aisle_id', updateableDate.aisle_id ?? '');
      formik.setFieldValue('x', updateableDate.x ?? 0);
      formik.setFieldValue('y', updateableDate.y ?? 0);
      formik.setFieldValue('capabilities', updateableDate.capabilities ?? ['counts']);
      formik.setFieldValue('ip', updateableDate.config?.ip ?? '');
    }
    return () => {
      dispatch(clearAddDisplayDeviceState());
      dispatch(clearUpdateDisplayDeviceState());
      formik.resetForm();
    };
  }, [updateableDate]);

  const diagramSrc = useMemo(() => {
    const lv = levels.find(
      (l: any) => String(l?._id) === String(formik.values.level_id)
    );
    const diagram = lv?.level_diagram;
    if (typeof diagram !== 'string' || diagram.length === 0) return null;
    return `${process.env.REACT_APP_API_URL}/${diagram}`;
  }, [levels, formik.values.level_id]);

  const handleCapabilityChange = (cap: string, checked: boolean) => {
    const current: string[] = formik.values.capabilities;
    if (checked) {
      formik.setFieldValue('capabilities', [...current, cap]);
    } else {
      formik.setFieldValue('capabilities', current.filter((c) => c !== cap));
    }
  };

  return (
    <form
      onSubmit={formik.handleSubmit}
      onReset={formik.handleReset}
      autoComplete="off"
      style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '480px' }}>

      <TextField
        label="Device ID"
        name="device_id"
        value={formik.values.device_id}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.device_id && Boolean(formik.errors.device_id)}
        helperText={formik.touched.device_id && formik.errors.device_id as string}
        size="small"
        fullWidth
      />

      <TextField
        label="Label"
        name="label"
        value={formik.values.label}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.label && Boolean(formik.errors.label)}
        helperText={formik.touched.label && formik.errors.label as string}
        size="small"
        fullWidth
      />

      <TextField
        select
        label="Type"
        name="type"
        value={formik.values.type}
        onChange={formik.handleChange}
        size="small"
        fullWidth>
        {DEVICE_TYPES.map((t) => (
          <MenuItem key={t} value={t}>{t}</MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Mode"
        name="mode"
        value={formik.values.mode}
        onChange={formik.handleChange}
        size="small"
        fullWidth>
        {DEVICE_MODES.map((m) => (
          <MenuItem key={m} value={m}>{m}</MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Level (optional)"
        name="level_id"
        value={formik.values.level_id}
        onChange={formik.handleChange}
        size="small"
        fullWidth>
        <MenuItem value="">— None —</MenuItem>
        {levels.map((lv: any) => (
          <MenuItem key={lv._id} value={lv._id}>
            Level {lv.level_id}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="Aisle ID (optional)"
        name="aisle_id"
        value={formik.values.aisle_id}
        onChange={formik.handleChange}
        size="small"
        fullWidth
      />

      <DiagramPicker
        src={diagramSrc}
        label="Pick device position on level diagram"
        value={{ x: formik.values.x, y: formik.values.y }}
        onPick={({ x, y }) => {
          formik.setFieldValue('x', x);
          formik.setFieldValue('y', y);
        }}
      />

      <div style={{ display: 'flex', gap: '12px' }}>
        <TextField
          label="X position"
          name="x"
          type="number"
          value={formik.values.x}
          onChange={formik.handleChange}
          size="small"
          fullWidth
        />
        <TextField
          label="Y position"
          name="y"
          type="number"
          value={formik.values.y}
          onChange={formik.handleChange}
          size="small"
          fullWidth
        />
      </div>

      <TextField
        label="Device IP (optional)"
        name="ip"
        value={formik.values.ip}
        onChange={formik.handleChange}
        size="small"
        fullWidth
        placeholder="e.g. 10.0.1.160"
      />

      <div>
        <FormLabel component="legend" style={{ fontSize: '13px', marginBottom: '4px' }}>
          Capabilities
        </FormLabel>
        <FormGroup row>
          {CAPABILITIES.map((cap) => (
            <FormControlLabel
              key={cap}
              control={
                <Checkbox
                  checked={formik.values.capabilities.includes(cap)}
                  onChange={(e) => handleCapabilityChange(cap, e.target.checked)}
                  size="small"
                />
              }
              label={cap}
            />
          ))}
        </FormGroup>
      </div>

      <button type="submit" style={{ display: 'none' }} />
    </form>
  );
};

export default DisplayForm;
